/**
 * components/landing/SiteFooter.jsx — Enterprise multi-column footer.
 *
 * Sections:
 *   • Newsletter (email capture)
 *   • Product links
 *   • Resources
 *   • Company
 *   • Status pill
 *   • Copyright
 *
 * A CTA banner is rendered right above the footer via <FooterCta>
 * (also exported from this file).  Pages can compose it before <Footer />
 * for a consistent bottom-of-page conversion pattern.
 *
 * 06/2026 — Removed Legal column, Documentation link, social icons,
 * version pill and SOC 2 attestation per production feedback.  Social
 * links will be reintroduced once official Kalki Ai Cloud handles are live.
 */

import Link from '@/shims/NextLink'
import { useState } from 'react'
import { toast } from 'sonner'
import { ArrowRight } from 'lucide-react'
import BrandLogo from './BrandLogo'
import { Container, GradientButton, GhostButton } from './design-system'

const PRODUCT_LINKS = [
  { label: 'Platform',    href: '/platform' },
  { label: 'AI',          href: '/ai' },
  { label: 'Trading',     href: '/trading' },
  { label: 'White-Label', href: '/white-label' },
  { label: 'Pricing',     href: '/pricing' },
]
const RESOURCE_LINKS = [
  // 10/2026 · Job 9 — Kalki Ai Cloud Dashboard Login footer entry.  Lives at the
  //   top of the Resources column so it's clearly visible on both
  //   desktop (3-col footer grid) and mobile (stacked single-column).
  //   Routes to the Kalki Ai Cloud tenant login page, which is a different
  //   subpath than any marketing page so we flag `external` so the
  //   Link renders without the client-side router (avoids a dead
  //   stale-cache state).
  { label: 'Kalki Ai Cloud Dashboard Login', href: 'https://kalkiai.trade/t/kalki/login', external: true, accent: true },
  { label: 'Insights',      href: '/resources' },
  { label: 'Get Started',   href: '/get-started' },
  { label: 'Changelog',     href: '/resources#changelog' },
  { label: 'Status',        href: '/trust#status' },
]
const COMPANY_LINKS = [
  { label: 'About',      href: '/company' },
  { label: 'Contact',    href: '/contact' },
  { label: 'Schedule Demo', href: '/contact#demo' },
  { label: 'Trust Center',  href: '/trust' },
]

// ─── CTA banner ───────────────────────────────────────────────────
export const FooterCta = () => (
  <section className="relative overflow-hidden">
    <Container>
      <div className="relative my-20 md:my-28 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-brand-gold/[0.12] via-transparent to-brand-emerald/[0.08] p-8 md:p-14">
        <img src="https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" aria-hidden="true" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/90" />
        <div
          className="pointer-events-none absolute inset-0 -z-10 opacity-60"
          style={{ background: 'radial-gradient(50% 60% at 30% 40%, rgba(255,193,7,0.18), transparent 60%), radial-gradient(40% 50% at 80% 60%, rgba(16,185,129,0.14), transparent 60%)' }}
        />
        <div className="relative grid gap-8 md:grid-cols-[1.4fr,1fr] items-center">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/25 bg-brand-gold/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-gold animate-pulse" />
              Ready to launch
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white leading-tight">
              Launch your own AI trading platform.<br />
              <span className="text-zinc-400">Under your brand, in weeks — not years.</span>
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 md:justify-end">
            <GradientButton href="/get-started" size="lg">Launch Platform</GradientButton>
            <GhostButton href="/contact" size="lg">Schedule Demo</GhostButton>
          </div>
        </div>
      </div>
    </Container>
  </section>
)

// ─── Newsletter capture ───────────────────────────────────────────
const Newsletter = () => {
  const [email, setEmail] = useState('')
  const [busy, setBusy]   = useState(false)
  const submit = async (e) => {
    e.preventDefault()
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error('Please enter a valid email')
    setBusy(true)
    try {
      const r = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ kind: 'newsletter', email }),
      })
      if (!r.ok) throw new Error('Subscription failed')
      toast.success('Subscribed', { description: 'You are on the Kalki Ai Cloud updates list.' })
      setEmail('')
    } catch (e) { toast.error(e.message || 'Something went wrong') }
    finally { setBusy(false) }
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <label htmlFor="brand-newsletter" className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">
        Product updates in your inbox
      </label>
      <div className="flex gap-2">
        <input
          id="brand-newsletter"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="flex-1 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-gold/50 focus:ring-2 focus:ring-brand-gold/20"
        />
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-full bg-white text-black px-4 py-2.5 text-sm font-semibold hover:bg-zinc-200 transition-colors disabled:opacity-50"
        >
          {busy ? '…' : (<>Join<ArrowRight className="h-3.5 w-3.5" /></>)}
        </button>
      </div>
      <div className="text-[11px] text-zinc-500">No spam. Unsubscribe anytime.</div>
    </form>
  )
}

// ─── Main footer ──────────────────────────────────────────────────
const SiteFooter = () => (
  <footer className="relative mt-6 border-t border-white/5 bg-black">
    <Container>
      <div className="grid gap-10 py-16 md:grid-cols-12">
        <div className="md:col-span-5 space-y-6">
          <BrandLogo size="md" />
          <p className="text-sm text-zinc-400 leading-relaxed max-w-sm">
            Kalki Ai Cloud is the enterprise infrastructure to launch and operate
            your own AI-powered trading platform under your brand.
          </p>
          <Newsletter />
        </div>
        <FooterCol title="Product"   items={PRODUCT_LINKS}  className="md:col-span-2" />
        <FooterCol title="Resources" items={RESOURCE_LINKS} className="md:col-span-2" />
        <FooterCol title="Company"   items={COMPANY_LINKS}  className="md:col-span-3" />
      </div>

      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 py-6 border-t border-white/5">
        <div className="flex items-center gap-3 text-[12px] text-zinc-500">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-brand-emerald">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald animate-pulse" />
            All systems operational
          </span>
        </div>
        <div className="text-[11px] text-zinc-600">
          © {new Date().getFullYear()} Kalki Ai Cloud — All rights reserved.
        </div>
      </div>
    </Container>
  </footer>
)

const FooterCol = ({ title, items, className = '' }) => (
  <div className={className}>
    <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-4">{title}</div>
    <ul className="space-y-2.5">
      {items.map((i) => {
        // 10/2026 · Job 9 — Support external links (opens new tab via
        //   anchor) + accent styling for the Kalki Ai Cloud Dashboard Login row.
        if (i.external) {
          return (
            <li key={i.href}>
              <a
                href={i.href}
                className={`text-sm transition-colors ${i.accent ? 'text-brand-gold font-semibold hover:text-brand-gold/80' : 'text-zinc-300 hover:text-white'}`}
                data-testid="footer-dashboard-login"
              >
                {i.label} →
              </a>
            </li>
          )
        }
        return (
          <li key={i.href}>
            <Link href={i.href} className="text-sm text-zinc-300 hover:text-white transition-colors">{i.label}</Link>
          </li>
        )
      })}
    </ul>
  </div>
)

export default SiteFooter
